/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService, IWrapHTTPConfig } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { EmptyResponse } from '../models/empty-response';
import { MalfunctionReasonForm } from '../models/malfunction-reason-form';
import { ResponseChatCountDto } from '../models/response-chat-count-dto';
import { ResponseInteger } from '../models/response-integer';
import { ResponseListMalfunctionReasonDto } from '../models/response-list-malfunction-reason-dto';
import { ResponseListSupportChatCompanyDto } from '../models/response-list-support-chat-company-dto';
import { ResponseListSupportChatCountryDto } from '../models/response-list-support-chat-country-dto';
import { ResponseListSupportChatThemeDto } from '../models/response-list-support-chat-theme-dto';
import { ResponseListSupportChatUserDto } from '../models/response-list-support-chat-user-dto';
import { ResponseMalfunctionReasonDto } from '../models/response-malfunction-reason-dto';
import { ResponseSupportChatItemDto } from '../models/response-support-chat-item-dto';
import { ResponseSupportChatListTotalDto } from '../models/response-support-chat-list-total-dto';
import { ResponseSupportChatMessageDto } from '../models/response-support-chat-message-dto';
import { ResponseSupportChatReportDto } from '../models/response-support-chat-report-dto';
import { ResponseTimestampListDtoSupportChatMessageDto } from '../models/response-timestamp-list-dto-support-chat-message-dto';
import { SupportChatCompanyForm } from '../models/support-chat-company-form';
import { SupportChatCreateForm } from '../models/support-chat-create-form';
import { SupportChatCreateTechnicalForm } from '../models/support-chat-create-technical-form';
import { SupportChatMessageTypeForm } from '../models/support-chat-message-type-form';
import { SupportChatMessagesForm } from '../models/support-chat-messages-form';
import { SupportChatMessagesReadForm } from '../models/support-chat-messages-read-form';
import { SupportChatReplyForm } from '../models/support-chat-reply-form';
import { SupportChatReportForm } from '../models/support-chat-report-form';
import { SupportChatSearchForm } from '../models/support-chat-search-form';
import { SupportChatStateCountForm } from '../models/support-chat-state-count-form';
import { SupportChatStateForm } from '../models/support-chat-state-form';
import { SupportChatThemeForm } from '../models/support-chat-theme-form';

@Injectable({
  providedIn: 'root',
})
export class AdminSupportApiService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getSupportChatReport
   */
  static readonly GetSupportChatReportPath = '/api/v1/admin/support/chat/{chatId}/report';

  /**
   * Get support chat report form
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSupportChatReport()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getSupportChatReport$Response(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatReportDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetSupportChatReportPath, 'get');
    if (params) {
      rb.path('chatId', params.chatId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatReportDto>;
      })
    );
  }

  /**
   * Get support chat report form
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getSupportChatReport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSupportChatReport(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatReportDto> {

    return this.wrapHTTPQuery(this.getSupportChatReport$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatReportDto>) => r.body as ResponseSupportChatReportDto)
    ), config);
  }

  /**
   * Path part for operation putSupportChatReport
   */
  static readonly PutSupportChatReportPath = '/api/v1/admin/support/chat/{chatId}/report';

  /**
   * Update support chat report form
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `putSupportChatReport()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private putSupportChatReport$Response(params: {
    chatId: number;
    body: SupportChatReportForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatReportDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.PutSupportChatReportPath, 'put');
    if (params) {
      rb.path('chatId', params.chatId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatReportDto>;
      })
    );
  }

  /**
   * Update support chat report form
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `putSupportChatReport$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  putSupportChatReport(params: {
    chatId: number;
    body: SupportChatReportForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatReportDto> {

    return this.wrapHTTPQuery(this.putSupportChatReport$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatReportDto>) => r.body as ResponseSupportChatReportDto)
    ), config);
  }

  /**
   * Path part for operation getSupportUsersForSupportChat
   */
  static readonly GetSupportUsersForSupportChatPath = '/api/v1/admin/support/user';

  /**
   * Get support users for support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSupportUsersForSupportChat()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getSupportUsersForSupportChat$Response(params?: {
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseListSupportChatUserDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetSupportUsersForSupportChatPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseListSupportChatUserDto>;
      })
    );
  }

  /**
   * Get support users for support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getSupportUsersForSupportChat$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSupportUsersForSupportChat(params?: {
  }, config?: IWrapHTTPConfig
): Observable<ResponseListSupportChatUserDto> {

    return this.wrapHTTPQuery(this.getSupportUsersForSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseListSupportChatUserDto>) => r.body as ResponseListSupportChatUserDto)
    ), config);
  }

  /**
   * Path part for operation getCountForSupportUnreadChat
   */
  static readonly GetCountForSupportUnreadChatPath = '/api/v1/admin/support/unread/count';

  /**
   * Get count for support unread chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCountForSupportUnreadChat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private getCountForSupportUnreadChat$Response(params: {
    body: SupportChatStateCountForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseInteger>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetCountForSupportUnreadChatPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseInteger>;
      })
    );
  }

  /**
   * Get count for support unread chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCountForSupportUnreadChat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getCountForSupportUnreadChat(params: {
    body: SupportChatStateCountForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseInteger> {

    return this.wrapHTTPQuery(this.getCountForSupportUnreadChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseInteger>) => r.body as ResponseInteger)
    ), config);
  }

  /**
   * Path part for operation getThemesForSupportChat
   */
  static readonly GetThemesForSupportChatPath = '/api/v1/admin/support/theme';

  /**
   * Get themes for support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getThemesForSupportChat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private getThemesForSupportChat$Response(params: {
    body: SupportChatThemeForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseListSupportChatThemeDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetThemesForSupportChatPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseListSupportChatThemeDto>;
      })
    );
  }

  /**
   * Get themes for support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getThemesForSupportChat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getThemesForSupportChat(params: {
    body: SupportChatThemeForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseListSupportChatThemeDto> {

    return this.wrapHTTPQuery(this.getThemesForSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseListSupportChatThemeDto>) => r.body as ResponseListSupportChatThemeDto)
    ), config);
  }

  /**
   * Path part for operation getCountStateForSupportChat
   */
  static readonly GetCountStateForSupportChatPath = '/api/v1/admin/support/state/{state}/count';

  /**
   * Get count state for support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCountStateForSupportChat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private getCountStateForSupportChat$Response(params: {
    state: 'SUPPORT_NEW' | 'SUPPORT_OPEN' | 'SUPPORT_CLOSED';
    body: SupportChatStateCountForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseInteger>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetCountStateForSupportChatPath, 'post');
    if (params) {
      rb.path('state', params.state, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseInteger>;
      })
    );
  }

  /**
   * Get count state for support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCountStateForSupportChat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getCountStateForSupportChat(params: {
    state: 'SUPPORT_NEW' | 'SUPPORT_OPEN' | 'SUPPORT_CLOSED';
    body: SupportChatStateCountForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseInteger> {

    return this.wrapHTTPQuery(this.getCountStateForSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseInteger>) => r.body as ResponseInteger)
    ), config);
  }

  /**
   * Path part for operation searchSupportChats
   */
  static readonly SearchSupportChatsPath = '/api/v1/admin/support/search';

  /**
   * Search support chats
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchSupportChats()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private searchSupportChats$Response(params: {
    body: SupportChatSearchForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatListTotalDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.SearchSupportChatsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatListTotalDto>;
      })
    );
  }

  /**
   * Search support chats
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `searchSupportChats$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchSupportChats(params: {
    body: SupportChatSearchForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatListTotalDto> {

    return this.wrapHTTPQuery(this.searchSupportChats$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatListTotalDto>) => r.body as ResponseSupportChatListTotalDto)
    ), config);
  }

  /**
   * Path part for operation getMalfunctionReasons
   */
  static readonly GetMalfunctionReasonsPath = '/api/v1/admin/support/malfunctionReasons';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMalfunctionReasons()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getMalfunctionReasons$Response(params?: {
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseListMalfunctionReasonDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetMalfunctionReasonsPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseListMalfunctionReasonDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getMalfunctionReasons$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMalfunctionReasons(params?: {
  }, config?: IWrapHTTPConfig
): Observable<ResponseListMalfunctionReasonDto> {

    return this.wrapHTTPQuery(this.getMalfunctionReasons$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseListMalfunctionReasonDto>) => r.body as ResponseListMalfunctionReasonDto)
    ), config);
  }

  /**
   * Path part for operation createMalfunctionReason
   */
  static readonly CreateMalfunctionReasonPath = '/api/v1/admin/support/malfunctionReasons';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createMalfunctionReason()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private createMalfunctionReason$Response(params: {
    body: MalfunctionReasonForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseMalfunctionReasonDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.CreateMalfunctionReasonPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseMalfunctionReasonDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createMalfunctionReason$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMalfunctionReason(params: {
    body: MalfunctionReasonForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseMalfunctionReasonDto> {

    return this.wrapHTTPQuery(this.createMalfunctionReason$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseMalfunctionReasonDto>) => r.body as ResponseMalfunctionReasonDto)
    ), config);
  }

  /**
   * Path part for operation createSupportChat
   */
  static readonly CreateSupportChatPath = '/api/v1/admin/support/create';

  /**
   * Create support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createSupportChat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private createSupportChat$Response(params: {
    body: SupportChatCreateForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.CreateSupportChatPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatItemDto>;
      })
    );
  }

  /**
   * Create support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createSupportChat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSupportChat(params: {
    body: SupportChatCreateForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatItemDto> {

    return this.wrapHTTPQuery(this.createSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatItemDto>) => r.body as ResponseSupportChatItemDto)
    ), config);
  }

  /**
   * Path part for operation getCountriesForSupportChat
   */
  static readonly GetCountriesForSupportChatPath = '/api/v1/admin/support/country';

  /**
   * Get countries for support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCountriesForSupportChat()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getCountriesForSupportChat$Response(params?: {
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseListSupportChatCountryDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetCountriesForSupportChatPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseListSupportChatCountryDto>;
      })
    );
  }

  /**
   * Get countries for support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCountriesForSupportChat$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCountriesForSupportChat(params?: {
  }, config?: IWrapHTTPConfig
): Observable<ResponseListSupportChatCountryDto> {

    return this.wrapHTTPQuery(this.getCountriesForSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseListSupportChatCountryDto>) => r.body as ResponseListSupportChatCountryDto)
    ), config);
  }

  /**
   * Path part for operation getCountForSupportChats
   */
  static readonly GetCountForSupportChatsPath = '/api/v1/admin/support/count/openNewUnread';

  /**
   * Get count for new and open chats with unread stats
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCountForSupportChats()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private getCountForSupportChats$Response(params: {
    body: SupportChatStateCountForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseChatCountDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetCountForSupportChatsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseChatCountDto>;
      })
    );
  }

  /**
   * Get count for new and open chats with unread stats
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCountForSupportChats$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getCountForSupportChats(params: {
    body: SupportChatStateCountForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseChatCountDto> {

    return this.wrapHTTPQuery(this.getCountForSupportChats$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseChatCountDto>) => r.body as ResponseChatCountDto)
    ), config);
  }

  /**
   * Path part for operation getCountForSupportChat
   */
  static readonly GetCountForSupportChatPath = '/api/v1/admin/support/count';

  /**
   * Get count for support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCountForSupportChat()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getCountForSupportChat$Response(params?: {
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseInteger>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetCountForSupportChatPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseInteger>;
      })
    );
  }

  /**
   * Get count for support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCountForSupportChat$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCountForSupportChat(params?: {
  }, config?: IWrapHTTPConfig
): Observable<ResponseInteger> {

    return this.wrapHTTPQuery(this.getCountForSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseInteger>) => r.body as ResponseInteger)
    ), config);
  }

  /**
   * Path part for operation getCompaniesForSupportChat
   */
  static readonly GetCompaniesForSupportChatPath = '/api/v1/admin/support/company';

  /**
   * Get companies for support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCompaniesForSupportChat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private getCompaniesForSupportChat$Response(params: {
    body: SupportChatCompanyForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseListSupportChatCompanyDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetCompaniesForSupportChatPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseListSupportChatCompanyDto>;
      })
    );
  }

  /**
   * Get companies for support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCompaniesForSupportChat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getCompaniesForSupportChat(params: {
    body: SupportChatCompanyForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseListSupportChatCompanyDto> {

    return this.wrapHTTPQuery(this.getCompaniesForSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseListSupportChatCompanyDto>) => r.body as ResponseListSupportChatCompanyDto)
    ), config);
  }

  /**
   * Path part for operation changeSupportChatState
   */
  static readonly ChangeSupportChatStatePath = '/api/v1/admin/support/chat/{chatId}/state';

  /**
   * Change support chat status
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `changeSupportChatState()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private changeSupportChatState$Response(params: {
    chatId: number;
    body: SupportChatStateForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.ChangeSupportChatStatePath, 'post');
    if (params) {
      rb.path('chatId', params.chatId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatItemDto>;
      })
    );
  }

  /**
   * Change support chat status
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `changeSupportChatState$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  changeSupportChatState(params: {
    chatId: number;
    body: SupportChatStateForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatItemDto> {

    return this.wrapHTTPQuery(this.changeSupportChatState$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatItemDto>) => r.body as ResponseSupportChatItemDto)
    ), config);
  }

  /**
   * Path part for operation approveSupportChatReport
   */
  static readonly ApproveSupportChatReportPath = '/api/v1/admin/support/chat/{chatId}/report/approve';

  /**
   * Approve support chat report form
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `approveSupportChatReport()` instead.
   *
   * This method doesn't expect any request body.
   */
  private approveSupportChatReport$Response(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.ApproveSupportChatReportPath, 'post');
    if (params) {
      rb.path('chatId', params.chatId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Approve support chat report form
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `approveSupportChatReport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  approveSupportChatReport(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.approveSupportChatReport$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation replyToSupportChat
   */
  static readonly ReplyToSupportChatPath = '/api/v1/admin/support/chat/{chatId}/reply';

  /**
   * Reply to support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `replyToSupportChat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private replyToSupportChat$Response(params: {
    chatId: number;
    body: SupportChatReplyForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.ReplyToSupportChatPath, 'post');
    if (params) {
      rb.path('chatId', params.chatId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatItemDto>;
      })
    );
  }

  /**
   * Reply to support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `replyToSupportChat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replyToSupportChat(params: {
    chatId: number;
    body: SupportChatReplyForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatItemDto> {

    return this.wrapHTTPQuery(this.replyToSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatItemDto>) => r.body as ResponseSupportChatItemDto)
    ), config);
  }

  /**
   * Path part for operation producerJoinToDistributorSupportChat
   */
  static readonly ProducerJoinToDistributorSupportChatPath = '/api/v1/admin/support/chat/{chatId}/producerJoin';

  /**
   * Producer join to distributor support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `producerJoinToDistributorSupportChat()` instead.
   *
   * This method doesn't expect any request body.
   */
  private producerJoinToDistributorSupportChat$Response(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.ProducerJoinToDistributorSupportChatPath, 'post');
    if (params) {
      rb.path('chatId', params.chatId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Producer join to distributor support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `producerJoinToDistributorSupportChat$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  producerJoinToDistributorSupportChat(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.producerJoinToDistributorSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation getMessagesForSupportChat
   */
  static readonly GetMessagesForSupportChatPath = '/api/v1/admin/support/chat/{chatId}/messages';

  /**
   * Get messages for support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMessagesForSupportChat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private getMessagesForSupportChat$Response(params: {
    chatId: number;
    body: SupportChatMessagesForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseTimestampListDtoSupportChatMessageDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetMessagesForSupportChatPath, 'post');
    if (params) {
      rb.path('chatId', params.chatId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseTimestampListDtoSupportChatMessageDto>;
      })
    );
  }

  /**
   * Get messages for support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getMessagesForSupportChat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getMessagesForSupportChat(params: {
    chatId: number;
    body: SupportChatMessagesForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseTimestampListDtoSupportChatMessageDto> {

    return this.wrapHTTPQuery(this.getMessagesForSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseTimestampListDtoSupportChatMessageDto>) => r.body as ResponseTimestampListDtoSupportChatMessageDto)
    ), config);
  }

  /**
   * Path part for operation closeSupportChat
   */
  static readonly CloseSupportChatPath = '/api/v1/admin/support/chat/{chatId}/close';

  /**
   * Close support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `closeSupportChat()` instead.
   *
   * This method doesn't expect any request body.
   */
  private closeSupportChat$Response(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatMessageDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.CloseSupportChatPath, 'post');
    if (params) {
      rb.path('chatId', params.chatId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatMessageDto>;
      })
    );
  }

  /**
   * Close support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `closeSupportChat$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  closeSupportChat(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatMessageDto> {

    return this.wrapHTTPQuery(this.closeSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatMessageDto>) => r.body as ResponseSupportChatMessageDto)
    ), config);
  }

  /**
   * Path part for operation readMessagesForSupportChat
   */
  static readonly ReadMessagesForSupportChatPath = '/api/v1/admin/support/chat/messages/read';

  /**
   * Read messages for support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `readMessagesForSupportChat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private readMessagesForSupportChat$Response(params: {
    body: SupportChatMessagesReadForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.ReadMessagesForSupportChatPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * Read messages for support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `readMessagesForSupportChat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  readMessagesForSupportChat(params: {
    body: SupportChatMessagesReadForm
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.readMessagesForSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

  /**
   * Path part for operation sendSupportChatFeedback
   */
  static readonly SendSupportChatFeedbackPath = '/api/v1/admin/support/chat/feedback';

  /**
   * Send support chat feedback
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `sendSupportChatFeedback()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private sendSupportChatFeedback$Response(params: {
    body: SupportChatMessageTypeForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatMessageDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.SendSupportChatFeedbackPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatMessageDto>;
      })
    );
  }

  /**
   * Send support chat feedback
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `sendSupportChatFeedback$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendSupportChatFeedback(params: {
    body: SupportChatMessageTypeForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatMessageDto> {

    return this.wrapHTTPQuery(this.sendSupportChatFeedback$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatMessageDto>) => r.body as ResponseSupportChatMessageDto)
    ), config);
  }

  /**
   * Path part for operation createTechnicalChat
   */
  static readonly CreateTechnicalChatPath = '/api/v1/admin/support/chat/createTechnicalChat';

  /**
   * Create technical chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTechnicalChat()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  private createTechnicalChat$Response(params: {
    body: SupportChatCreateTechnicalForm
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.CreateTechnicalChatPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatItemDto>;
      })
    );
  }

  /**
   * Create technical chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createTechnicalChat$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTechnicalChat(params: {
    body: SupportChatCreateTechnicalForm
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatItemDto> {

    return this.wrapHTTPQuery(this.createTechnicalChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatItemDto>) => r.body as ResponseSupportChatItemDto)
    ), config);
  }

  /**
   * Path part for operation getSupportChat
   */
  static readonly GetSupportChatPath = '/api/v1/admin/support/chat/{chatId}';

  /**
   * Get support chat
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSupportChat()` instead.
   *
   * This method doesn't expect any request body.
   */
  private getSupportChat$Response(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<ResponseSupportChatItemDto>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.GetSupportChatPath, 'get');
    if (params) {
      rb.path('chatId', params.chatId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ResponseSupportChatItemDto>;
      })
    );
  }

  /**
   * Get support chat
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getSupportChat$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSupportChat(params: {
    chatId: number;
  }, config?: IWrapHTTPConfig
): Observable<ResponseSupportChatItemDto> {

    return this.wrapHTTPQuery(this.getSupportChat$Response(params, config).pipe(
      map((r: StrictHttpResponse<ResponseSupportChatItemDto>) => r.body as ResponseSupportChatItemDto)
    ), config);
  }

  /**
   * Path part for operation deleteMalfunctionReason
   */
  static readonly DeleteMalfunctionReasonPath = '/api/v1/admin/support/malfunctionReasons/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteMalfunctionReason()` instead.
   *
   * This method doesn't expect any request body.
   */
  private deleteMalfunctionReason$Response(params: {
    id: number;
  }, config?: IWrapHTTPConfig
): Observable<StrictHttpResponse<EmptyResponse>> {

    const rb = new RequestBuilder(this.rootUrl, AdminSupportApiService.DeleteMalfunctionReasonPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    }, config)).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptyResponse>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteMalfunctionReason$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteMalfunctionReason(params: {
    id: number;
  }, config?: IWrapHTTPConfig
): Observable<EmptyResponse> {

    return this.wrapHTTPQuery(this.deleteMalfunctionReason$Response(params, config).pipe(
      map((r: StrictHttpResponse<EmptyResponse>) => r.body as EmptyResponse)
    ), config);
  }

}
